import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchableDropdown, { DropdownOption } from './SearchableDropdown';
import { getTableData, loadTable } from '../../features/db-viewer/data/dataAccess';
import './PhoneInput.css';

// =============================================
// PhoneInput — international phone number entry (country code + number)
// =============================================
// Akountant is a global product: a phone number is never assumed to be Canadian /
// North American. Every number carries an explicit country calling code, picked
// from site_mstr_countries (searchable by country name or code), and is stored in
// the existing users/business phone columns as  +<code>-<number>  (no schema
// change; callers that strip to +digits still round-trip via longest-prefix match).
// =============================================

export interface DialCountry {
  iso2: string;
  name: string;
  dial: string; // digits only, e.g. "1", "44", "1242"
}

/** site_mstr_countries.phonecode → digits ("+1-242" → "1242"; "1-809 and 1-829" → "1809"). */
export function normalizeDialCode(phonecode: unknown): string {
  const first = String(phonecode ?? '').split(/and|,|\//i)[0];
  return first.replace(/\D/g, '');
}

export function toDialCountries(rows: Record<string, any>[]): DialCountry[] {
  return rows
    .filter(r => r.is_active == null || Number(r.is_active) === 1)
    .map(r => ({ iso2: String(r.iso2 || r.name || ''), name: String(r.name || ''), dial: normalizeDialCode(r.phonecode) }))
    .filter(c => c.iso2 && c.name && c.dial)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Split a stored value into calling code + national part. Unknown / legacy → dial ''. */
export function parsePhone(value: string, dialCodes: Set<string>): { dial: string; national: string } {
  const v = (value || '').trim();
  if (!v.startsWith('+')) return { dial: '', national: v };
  const rest = v.slice(1).trim();
  const sep = rest.match(/^(\d{1,4})[-\s](.*)$/);
  if (sep && dialCodes.has(sep[1])) return { dial: sep[1], national: sep[2] };
  const lead = (rest.match(/^\d+/) || [''])[0];
  for (let len = Math.min(4, lead.length); len >= 1; len--) {
    const code = lead.slice(0, len);
    if (dialCodes.has(code)) return { dial: code, national: rest.slice(len).replace(/^[-\s]+/, '') };
  }
  return { dial: '', national: v };
}

/** '' when acceptable, otherwise the message to show. Empty value is valid (use `required`). */
export function phoneProblem(dial: string, national: string): string {
  const digits = national.replace(/\D/g, '');
  if (!national.trim()) return '';
  if (!dial) return 'Select the country code for this phone number.';
  if (/[^\d\s().-]/.test(national)) return 'Use digits only (spaces, dashes and brackets are allowed).';
  if (digits.length < 4) return 'This phone number is too short.';
  if (dial.length + digits.length > 15) return 'This phone number is too long (15 digits max including country code).';
  return '';
}

export interface PhoneInputProps {
  /** Stored value: "+<code>-<number>" (also accepts "+digits" and legacy free text). */
  value: string;
  onChange: (value: string) => void;
  /** Country (name or ISO-2) whose code is preselected for an empty field, e.g. the address country. */
  defaultCountry?: string;
  required?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  /** Class on the outer wrapper (layout: width / flex). */
  className?: string;
  /** Class on the number <input> (visual style, e.g. "fb-input"). */
  inputClassName?: string;
  placeholder?: string;
}

let countriesCache: DialCountry[] | null = null;

const PhoneInput: React.FC<PhoneInputProps> = ({
  value, onChange, defaultCountry, required, disabled, hasError, className = '', inputClassName = '', placeholder = 'Phone number',
}) => {
  const [countries, setCountries] = useState<DialCountry[]>(
    () => countriesCache || toDialCountries(getTableData('site_mstr_countries')),
  );
  useEffect(() => {
    if (countriesCache) return;
    let cancelled = false;
    loadTable('site_mstr_countries').then(rows => {
      const list = toDialCountries(rows);
      if (list.length) countriesCache = list;
      if (!cancelled && list.length) setCountries(list);
    }).catch(() => { /* keep whatever the local cache had */ });
    return () => { cancelled = true; };
  }, []);

  const dialCodes = useMemo(() => new Set(countries.map(c => c.dial)), [countries]);
  const options = useMemo<DropdownOption[]>(
    () => countries.map(c => ({ value: c.iso2, label: `+${c.dial} ${c.name}` })),
    [countries],
  );
  const fallback = useMemo(() => {
    const d = (defaultCountry || '').trim().toLowerCase();
    return d ? countries.find(c => c.name.toLowerCase() === d || c.iso2.toLowerCase() === d) : undefined;
  }, [countries, defaultCountry]);

  const { dial, national } = parsePhone(value, dialCodes);

  // Several countries share a calling code (+1, +7, +44 …), so the picked country is
  // remembered here; the stored value only carries the code.
  const [pickedIso, setPickedIso] = useState('');
  const picked = countries.find(c => c.iso2 === pickedIso);
  const selected: DialCountry | undefined = dial
    ? (picked && picked.dial === dial ? picked
      : fallback && fallback.dial === dial ? fallback
      : countries.find(c => c.dial === dial))
    : (national ? undefined : (picked || fallback));

  const compose = (code: string, num: string) => (num.trim() ? (code ? `+${code}-${num}` : num) : '');

  const handleCountry = (iso2: string) => {
    setPickedIso(iso2);
    const c = countries.find(x => x.iso2 === iso2);
    if (c) onChange(compose(c.dial, national));
  };
  const handleNumber = (text: string) => {
    // A full international number typed/pasted here ("+44 20 …") sets the code too.
    if (text.trim().startsWith('+')) { onChange(text.trim()); return; }
    onChange(compose(selected?.dial || '', text));
  };

  const problem = phoneProblem(selected?.dial || '', national);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.setCustomValidity(problem); }, [problem]);

  return (
    <div className={`phone-input ${className}`}>
      <SearchableDropdown
        options={options}
        value={selected?.iso2 || ''}
        onChange={handleCountry}
        placeholder="Code"
        wrapperClassName="phone-input-code"
        hasError={hasError || problem.startsWith('Select')}
        disabled={disabled}
      />
      <input
        ref={inputRef}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={placeholder}
        value={national}
        onChange={e => handleNumber(e.target.value)}
        required={required}
        disabled={disabled}
        title={problem || undefined}
        aria-invalid={problem ? true : undefined}
        className={`phone-input-number ${inputClassName}`}
      />
    </div>
  );
};

export default PhoneInput;
